<template>
    <!-- Кнопка-печенька: открыть настройки в любой момент -->
    <v-btn v-if="!dialog" icon class="cookie-fab" :aria-label="$t('$vuetify.cookieConsent.manage')"
        @click="openSettings">
        <v-icon size="26">mdi-cookie-outline</v-icon>
    </v-btn>

    <v-dialog v-model="dialog" persistent max-width="560" scrollable>
        <v-card class="cookie-card pa-6" rounded="lg">
            <h2 class="cookie-title">{{ $t('$vuetify.cookieConsent.title') }}</h2>

            <p class="cookie-text mt-3">{{ $t('$vuetify.cookieConsent.text') }}</p>

            <!-- Настройки по категориям -->
            <div v-if="showSettings" class="cookie-settings mt-4">
                <div v-for="category in categories" :key="category.key" class="cookie-category">
                    <v-switch v-model="selected[category.key]" :disabled="category.key === 'necessary'" color="#316F46"
                        density="compact" hide-details :label="$t(`$vuetify.cookieConsent.categories.${category.key}`)">
                    </v-switch>
                    <p class="cookie-category__text">
                        {{ $t(`$vuetify.cookieConsent.descriptions.${category.key}`) }}
                    </p>
                </div>
            </div>

            <div class="cookie-actions mt-5">
                <v-btn class="cookie-btn" size="large" @click="rejectAll">
                    {{ $t('$vuetify.cookieConsent.reject') }}
                </v-btn>
                <v-btn v-if="!showSettings" class="cookie-btn" size="large" @click="showSettings = true">
                    {{ $t('$vuetify.cookieConsent.settings') }}
                </v-btn>
                <v-btn v-else class="cookie-btn" size="large" @click="saveSelection">
                    {{ $t('$vuetify.cookieConsent.save') }}
                </v-btn>
                <v-btn class="cookie-btn" size="large" @click="acceptAll">
                    {{ $t('$vuetify.cookieConsent.acceptAll') }}
                </v-btn>
            </div>

            <div class="cookie-links mt-4">
                <router-link :to="{ name: 'legal', params: { lang: $vuetify.locale.current }, hash: '#privacy' }"
                    class="cookie-link" @click="dialog = false">
                    {{ $t('$vuetify.footer.legal.privacy') }}
                </router-link>
                <router-link :to="{ name: 'legal', params: { lang: $vuetify.locale.current }, hash: '#terms' }"
                    class="cookie-link" @click="dialog = false">
                    {{ $t('$vuetify.footer.legal.terms') }}
                </router-link>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import { CONSENT_STORAGE_KEY as STORAGE_KEY, FUNCTIONAL_STORAGE_KEYS, readConsent } from '@/store/consent';

export default {
    name: 'CookieConsent',
    data: () => ({
        dialog: false,
        showSettings: false,
        categories: [
            { key: 'necessary' },
            { key: 'functional' },
            { key: 'analytics' },
            { key: 'marketing' },
        ],
        selected: {
            necessary: true,
            functional: false,
            analytics: false,
            marketing: false,
        }
    }),
    mounted() {
        const saved = readConsent();
        if (saved) {
            this.selected = {
                necessary: true,
                functional: !!saved.functional,
                analytics: !!saved.analytics,
                marketing: !!saved.marketing,
            };
            this.applyConsent(this.selected);
            // Данные могли остаться с прошлых визитов, когда категории ещё не гейтились
            this.cleanUp(this.selected);
        } else {
            // Согласия ещё нет: показываем окно, до выбора ничего не включаем
            this.dialog = true;
        }
    },
    methods: {

        storeConsent(consent) {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, date: new Date().toISOString() }));
            } catch (e) {
                // приватный режим браузера — согласие просто не переживёт перезагрузку
            }
        },
        applyConsent(consent) {
            if (typeof window.gtag !== 'function') return;

            // ads_data_redaction — отдельная команда с булевым значением:
            // внутри consent этот ключ просто игнорируется.
            window.gtag('set', 'ads_data_redaction', !consent.marketing);

            window.gtag('consent', 'update', {
                analytics_storage: consent.analytics ? 'granted' : 'denied',
                ad_storage: consent.marketing ? 'granted' : 'denied',
                ad_user_data: consent.marketing ? 'granted' : 'denied',
                ad_personalization: consent.marketing ? 'granted' : 'denied',
                functionality_storage: consent.functional ? 'granted' : 'denied',
                personalization_storage: consent.functional ? 'granted' : 'denied',
                security_storage: 'granted',
            });

            // gtag.js не загружен, пока нет согласия на аналитику
            if (consent.analytics && typeof window.hpLoadAnalytics === 'function') {
                window.hpLoadAnalytics();
            }
        },
        // Отзыв согласия должен убирать и то, что уже записано
        cleanUp(consent) {
            try {
                if (!consent.functional) {
                    FUNCTIONAL_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
                }
            } catch (e) {
                // приватный режим — чистить нечего
            }

            if (!consent.analytics) {
                try {
                    const host = window.location.hostname;
                    const domains = [...new Set([host, '.' + host, '.' + host.split('.').slice(-2).join('.')])];
                    document.cookie.split(';').forEach(entry => {
                        const name = entry.split('=')[0].trim();
                        // _ga, _ga_<id>, _gid, _gat — аналитика. _gac_/_gcl_ это Ads, их не трогаем
                        if (!/^(_ga($|_)|_gid$|_gat)/.test(name)) return;
                        domains.forEach(domain => {
                            document.cookie = `${name}=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
                        });
                        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
                    });
                } catch (e) {
                    // доступ к cookie заблокирован — чистить нечего
                }
            }
        },
        commit(consent) {
            this.selected = { ...consent, necessary: true };
            this.storeConsent(this.selected);
            this.applyConsent(this.selected);
            this.cleanUp(this.selected);
            this.showSettings = false;
            this.dialog = false;
        },
        acceptAll() {
            this.commit({ necessary: true, functional: true, analytics: true, marketing: true });
        },
        rejectAll() {
            this.commit({ necessary: true, functional: false, analytics: false, marketing: false });
        },
        saveSelection() {
            this.commit({ ...this.selected });
        },
        openSettings() {
            const saved = readConsent();
            if (saved) {
                this.selected = {
                    necessary: true,
                    functional: !!saved.functional,
                    analytics: !!saved.analytics,
                    marketing: !!saved.marketing,
                };
            }
            this.showSettings = true;
            this.dialog = true;
        }
    },
}
</script>

<style scoped>
.cookie-fab {
    position: fixed;
    left: 16px;
    bottom: 16px;
    z-index: 1005;
    background-color: #316F46;
    color: #F2F3ED;
    box-shadow: 0px 4px 11.1px 0px #00000040;
}

.cookie-card {
    background-color: #F2F3ED;
}

.cookie-title {
    font-size: 1.25rem;
    font-weight: 500;
    letter-spacing: -0.5px;
    color: #1B3D26;
}

.cookie-text {
    font-size: 0.85rem;
    line-height: 1.35rem;
    font-weight: 300;
    color: #2A3228;
}

.cookie-settings {
    max-height: 40vh;
    overflow-y: auto;
    /* overflow-y режет и по горизонтали: ripple свитча вылезает влево за трек,
       поэтому даём ему место padding'ом и возвращаем выравнивание margin'ом */
    padding-left: 10px;
    margin-left: -10px;
}

.cookie-category {
    padding-bottom: 4px;
}

.cookie-category__text {
    font-size: 0.8rem;
    line-height: 1.15rem;
    font-weight: 300;
    color: #5f6b60;
}

.cookie-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.cookie-btn {
    flex: 1 1 140px;
    background-color: #316F46;
    color: #F2F3ED;
    border-radius: 12px;
    text-transform: inherit;
    font-size: 0.9rem;
    font-weight: 500;
    letter-spacing: 0rem;
    box-shadow: none;
}

.cookie-links {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}

.cookie-link {
    font-size: 0.8rem;
    color: #316F46;
    text-decoration: underline;
}
</style>
